import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type {
  BitLockerAccessRow, BitLockerKeyInfo, BitLockerOverview, BitLockerRotationInfo, BitLockerSettings, BitLockerVolumeRow,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { usePermessiModuli } from '../auth/permessi'
import { MostraChiave, RuotaChiave, etichettaRotazione, tonoRotazione } from '../components/BitLockerChiave'
import type { ChiaveBitLocker } from '../components/BitLockerChiave'
import { Card, EmptyState, ErrorBanner, PageSection, SectionedPage, Spinner, StatCard, formatDateTime } from '../components/common'
import { DataTable } from '../components/DataTable'
import type { Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Sicurezza → BitLocker: the encryption of the Windows volumes and their
 * recovery keys.
 *
 * The CENSO agent reports every volume at each inventory; when the tenant
 * switches collection on, it also sends the recovery passwords, stored by the
 * Control Plane encrypted with the tenant key (never on the CI, never in a
 * mail). Sections:
 *
 * - «Trova una chiave», the helpdesk flow and the first thing on the page: the
 *   Key ID from the blue recovery screen (or the machine name) → the key →
 *   «Mostra la chiave», which asks a reason, is written down, notified and by
 *   default queues the rotation of the key;
 * - «Panoramica»: what is protected, what is not, what CENSO holds, and the
 *   collection settings (tenant admins);
 * - «Volumi»: every volume, with its keys (also the superseded ones) and the
 *   last rotation;
 * - «Rotazioni» and «Letture»: the two registers.
 *
 * See bitlocker_service and docs/architecture/bitlocker.md.
 */

type FiltroVolumi = 'tutti' | 'os_scoperti' | 'senza_chiave'

/** Rotations that can still be cancelled (the agent is not running them). */
const ANNULLABILI = ['queued', 'waiting_escrow', 'failed']

/** At least this many characters before the search starts (as the API). */
const MIN_RICERCA = 3

function etichettaTipoVolume(tipo: string): string {
  const etichette: Record<string, string> = {
    OperatingSystem: t('Sistema operativo'),
    FixedData: t('Dati'),
    Removable: t('Rimovibile'),
  }
  return etichette[tipo] ?? (tipo || '—')
}

function etichettaStatoVolume(stato: string): string {
  const etichette: Record<string, string> = {
    FullyEncrypted: t('Cifrato'),
    FullyDecrypted: t('Non cifrato'),
    EncryptionInProgress: t('Cifratura in corso'),
    DecryptionInProgress: t('Decifratura in corso'),
    EncryptionSuspended: t('Cifratura sospesa'),
    DecryptionSuspended: t('Decifratura sospesa'),
    EncryptionPaused: t('Cifratura sospesa'),
    DecryptionPaused: t('Decifratura sospesa'),
  }
  return etichette[stato] ?? (stato || '—')
}

function etichettaPasso(passo: string): string {
  const etichette: Record<string, string> = {
    add: t('aggiunge la nuova chiave'),
    remove: t('toglie la vecchia chiave'),
  }
  return etichette[passo] ?? passo
}

function etichettaProtezione(protezione: string): string {
  if (protezione === 'On') return t('attiva')
  if (protezione === 'Off') return t('spenta')
  return protezione === 'Unknown' || !protezione ? t('sconosciuta') : protezione
}

function BadgeProtezione({ protezione }: { protezione: string }) {
  const tono = protezione === 'On' ? 'ok' : protezione === 'Off' ? 'error' : 'warn'
  return <span className={`badge badge--${tono}`} title={protezione}>{etichettaProtezione(protezione)}</span>
}

function etichettaChiave(stato: string): string {
  return stato === 'attiva' ? t('attiva') : t('sostituita')
}

function BadgeChiave({ stato }: { stato: string }) {
  return <span className={`badge badge--${stato === 'attiva' ? 'ok' : 'muted'}`}>{etichettaChiave(stato)}</span>
}

function BadgeRotazione({ r }: { r: { status: string } | null | undefined }) {
  if (!r) return <span className="text-muted">—</span>
  return <span className={`badge badge--${tonoRotazione(r.status)}`} title={r.status}>{etichettaRotazione(r.status)}</span>
}

function Macchina({ id, nome }: { id: string | null | undefined; nome: string | null | undefined }) {
  const testo = nome || (id ? id.slice(0, 8) : '—')
  return id ? <Link to={`/cis/${id}`}>{testo}</Link> : <span>{testo}</span>
}

function KeyId({ id, grande = false, title }: { id: string; grande?: boolean; title?: string }) {
  return (
    <span className="mono" title={title}
      style={{ fontSize: grande ? 18 : 13, fontWeight: grande ? 700 : 400, letterSpacing: grande ? '0.08em' : undefined }}>
      {id || '—'}
    </span>
  )
}

/** The key a dialog acts on, from a key row and the name of its machine. */
function perDialogo(k: BitLockerKeyInfo, ciName?: string | null): ChiaveBitLocker {
  return { id: k.id, key_id: k.key_id, ci_name: k.ci_name ?? ciName ?? null, mount: k.mount }
}

export default function BitLocker() {
  const dati = useAsync(() => api.bitlocker(), [])
  const rotazioni = useAsync(() => api.bitlockerRotations(), [])
  const letture = useAsync(() => api.bitlockerAccess(), [])
  const scrive = usePermessiModuli().scrive('bitlocker')
  const { principal } = useAuth()
  const admin = Boolean(principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin'))

  const [mostra, setMostra] = useState<ChiaveBitLocker | null>(null)
  const [ruota, setRuota] = useState<ChiaveBitLocker | null>(null)
  const [filtroVolumi, setFiltroVolumi] = useState<FiltroVolumi>('tutti')
  // Bumped after every action, so the search asks again with the new counters.
  const [giro, setGiro] = useState(0)
  const [nota, setNota] = useState('')
  const [erroreAzione, setErroreAzione] = useState<unknown>(null)
  const [annullando, setAnnullando] = useState<string | null>(null)

  function ricarica() {
    void dati.reload()
    void rotazioni.reload()
    void letture.reload()
    setGiro((g) => g + 1)
  }

  function vaiAiVolumi(filtro: FiltroVolumi) {
    setFiltroVolumi(filtro)
    window.location.hash = 'volumi'
  }

  async function annulla(r: BitLockerRotationInfo) {
    const domanda = r.status === 'waiting_escrow'
      ? t('Annullare la rotazione della chiave {id}? L’agente ha già aggiunto la nuova chiave: sul volume restano tutte e due.', { id: r.old_key_id })
      : t('Annullare la rotazione della chiave {id}?', { id: r.old_key_id })
    if (!window.confirm(domanda)) return
    setAnnullando(r.id)
    setErroreAzione(null)
    setNota('')
    try {
      await api.cancelBitLockerRotation(r.id)
      setNota(t('Rotazione della chiave {id} annullata.', { id: r.old_key_id }))
      ricarica()
    } catch (e) {
      setErroreAzione(e)
    } finally {
      setAnnullando(null)
    }
  }

  const d = dati.data
  const nVolumi = d?.volumes.length ?? 0

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('BitLocker')}</h1>
          <p className="page__subtitle">
            {t('La cifratura BitLocker dei volumi Windows, letta dall’agente CENSO, e le chiavi di ripristino custodite cifrate. Ogni lettura di una chiave chiede un motivo, resta nel registro ed è notificata; dopo la lettura l’agente sostituisce la chiave.')}
          </p>
        </div>
        <button type="button" className="secondary" onClick={ricarica} disabled={dati.loading}>
          {dati.loading ? t('Caricamento…') : t('Aggiorna')}
        </button>
      </header>
      <ErrorBanner error={dati.error} />
      <ErrorBanner error={erroreAzione} />
      {nota && <div className="banner banner--ok">{nota}</div>}

      <SectionedPage
        sections={[
          { id: 'trova', label: t('Trova una chiave') },
          { id: 'panoramica', label: t('Panoramica') },
          { id: 'volumi', label: d ? t('Volumi ({n})', { n: nVolumi }) : t('Volumi') },
          { id: 'rotazioni', label: t('Rotazioni') },
          { id: 'letture', label: t('Letture') },
        ]}
      >
        <PageSection id="trova">
          <TrovaChiave
            impostazioni={d?.settings ?? null}
            scrive={scrive}
            giro={giro}
            onMostra={(k) => setMostra(perDialogo(k))}
          />
        </PageSection>

        <PageSection id="panoramica">
          {!d ? (!dati.error && <Spinner />) : (
            <>
              <Riepilogo d={d} onVolumi={vaiAiVolumi} />
              <SenzaDati d={d} scrive={scrive} onChiesto={() => void dati.reload()} />
              <Impostazioni s={d.settings} admin={admin} onSalvate={() => void dati.reload()} />
            </>
          )}
        </PageSection>

        <PageSection id="volumi">
          {!d ? (!dati.error && <Spinner />) : (
            <Volumi
              d={d}
              filtro={filtroVolumi}
              onFiltro={setFiltroVolumi}
              scrive={scrive}
              annullando={annullando}
              onMostra={(k, v) => setMostra(perDialogo(k, v.ci_name))}
              onRuota={(k, v) => setRuota(perDialogo(k, v.ci_name))}
              onAnnulla={(r) => void annulla(r)}
            />
          )}
        </PageSection>

        <PageSection id="rotazioni">
          <ErrorBanner error={rotazioni.error} />
          {!rotazioni.data ? (!rotazioni.error && <Spinner />) : (
            <Rotazioni righe={rotazioni.data} scrive={scrive} annullando={annullando} onAnnulla={(r) => void annulla(r)} />
          )}
        </PageSection>

        <PageSection id="letture">
          <ErrorBanner error={letture.error} />
          {!letture.data ? (!letture.error && <Spinner />) : <Letture righe={letture.data} />}
        </PageSection>
      </SectionedPage>

      {mostra && <MostraChiave chiave={mostra} onChiudi={() => setMostra(null)} onLetta={ricarica} />}
      {ruota && (
        <RuotaChiave
          chiave={ruota}
          onChiudi={() => setRuota(null)}
          onFatto={(r) => {
            setRuota(null)
            setErroreAzione(null)
            setNota(t('Rotazione della chiave {id} messa in coda: l’agente la esegue appena la macchina è in linea.', { id: r.old_key_id }))
            ricarica()
          }}
        />
      )}
    </>
  )
}

// ------------------------------------------------------------ find a key

/**
 * The helpdesk flow: the user reads the «Key ID» on the recovery screen (or
 * says the machine name), the operator types it here and reads the key.
 */
function TrovaChiave({
  impostazioni,
  scrive,
  giro,
  onMostra,
}: {
  impostazioni: BitLockerSettings | null
  scrive: boolean
  giro: number
  onMostra: (k: BitLockerKeyInfo) => void
}) {
  const [q, setQ] = useState('')
  const [cercata, setCercata] = useState('')
  const [righe, setRighe] = useState<BitLockerKeyInfo[] | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [cerco, setCerco] = useState(false)

  // Debounce: one request when the typing stops, not one per key.
  useEffect(() => {
    const id = window.setTimeout(() => setCercata(q.trim()), 300)
    return () => window.clearTimeout(id)
  }, [q])

  useEffect(() => {
    if (cercata.length < MIN_RICERCA) {
      setRighe(null)
      setErrore(null)
      setCerco(false)
      return
    }
    // An older answer arriving after a newer one must not win.
    let attuale = true
    setCerco(true)
    api.bitlockerSearch(cercata)
      .then((r) => { if (attuale) { setRighe(r); setErrore(null) } })
      .catch((e) => { if (attuale) setErrore(e) })
      .finally(() => { if (attuale) setCerco(false) })
    return () => { attuale = false }
  }, [cercata, giro])

  const colonne: Column<BitLockerKeyInfo>[] = [
    { key: 'ci_name', header: t('Macchina'), value: (k) => k.ci_name ?? '', render: (k) => <Macchina id={k.ci_id} nome={k.ci_name} /> },
    { key: 'mount', header: t('Volume'), value: (k) => `${k.mount} ${etichettaTipoVolume(k.volume_type)}`, render: (k) => (
      <><span className="mono">{k.mount || '—'}</span>{k.volume_type && <><br /><small className="text-muted">{etichettaTipoVolume(k.volume_type)}</small></>}</>
    ) },
    { key: 'key_id', header: t('ID chiave'), value: (k) => k.key_id, render: (k) => <KeyId id={k.key_id} grande title={k.protector_id} /> },
    { key: 'status', header: t('Stato'), value: (k) => etichettaChiave(k.status), render: (k) => <BadgeChiave stato={k.status} /> },
    { key: 'last_seen', header: t('Ultima vista'), type: 'date', value: (k) => k.last_seen, render: (k) => formatDateTime(k.last_seen) },
    { key: 'revealed_count', header: t('Letture'), type: 'number', value: (k) => k.revealed_count, render: (k) => (
      <span title={k.last_revealed_at ? t('Ultima lettura: {quando}, {chi}', { quando: formatDateTime(k.last_revealed_at), chi: k.last_revealed_by || '—' }) : undefined}>
        {k.revealed_count}
      </span>
    ) },
    ...(scrive ? [{
      key: 'azioni', header: '', sort: false, searchable: false, render: (k: BitLockerKeyInfo) => (
        <button type="button" className={k.status === 'attiva' ? undefined : 'secondary'} onClick={() => onMostra(k)}>
          {t('Mostra la chiave')}
        </button>
      ),
    }] : []),
  ]

  const breve = q.trim().length > 0 && q.trim().length < MIN_RICERCA

  return (
    <>
      <Card>
        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="bitlocker-cerca" style={{ fontSize: 16 }}>{t('Trova la chiave di ripristino')}</label>
          <input
            id="bitlocker-cerca"
            type="search"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            maxLength={80}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('ID chiave (le prime 8 cifre mostrate nella schermata di ripristino BitLocker) o nome della macchina')}
            style={{ fontSize: 20, padding: '14px 16px' }}
          />
          <div className="field__hint">
            {t('La schermata blu di ripristino mostra l’«ID chiave di ripristino» (in inglese «Recovery key ID»): bastano i primi caratteri. Si cerca anche per nome della macchina.')}
          </div>
        </div>
      </Card>

      {impostazioni && !impostazioni.collect_keys && (
        <div className="banner banner--warn">
          {t('La raccolta delle chiavi è spenta: CENSO conosce lo stato dei volumi ma non riceve nuove chiavi di ripristino. Un amministratore del tenant la accende in «Panoramica».')}
        </div>
      )}
      {!scrive && (
        <div className="banner banner--info">
          {t('Hai la sola lettura del modulo BitLocker: puoi cercare le chiavi ma non mostrarle.')}
        </div>
      )}
      <ErrorBanner error={errore} />

      {breve ? (
        <p className="field__hint">{t('Scrivi almeno {n} caratteri.', { n: MIN_RICERCA })}</p>
      ) : cercata.length < MIN_RICERCA ? null : cerco && !righe ? (
        <Spinner label={t('Cerco…')} />
      ) : righe && righe.length === 0 ? (
        <Card>
          <EmptyState
            title={t('Nessuna chiave trovata per «{q}»', { q: cercata })}
            hint={t('Controlla l’ID chiave sulla schermata di ripristino. Se la macchina è giusta ma la chiave manca, l’agente non l’ha ancora inviata: serve la raccolta accesa e un inventario dopo l’accensione.')}
          />
        </Card>
      ) : righe && (
        <Card>
          <DataTable
            id="bitlocker-trova"
            rows={righe}
            rowKey={(k) => k.id}
            columns={colonne}
            conteggio={{ nome: t('chiavi') }}
            paginazione={false}
            strumenti={false}
          />
          {righe.length >= 50 && (
            <p className="field__hint">{t('Sono mostrate le prime 50: scrivi qualche carattere in più.')}</p>
          )}
        </Card>
      )}
    </>
  )
}

// ------------------------------------------------------------- overview

function Riepilogo({ d, onVolumi }: { d: BitLockerOverview; onVolumi: (f: FiltroVolumi) => void }) {
  const s = d.summary
  // With no machine reporting yet, a green «0 protected» would say «all good».
  const dati = s.machines > 0
  return (
    <div className="stats">
      <StatCard label={t('Macchine con dati BitLocker')} value={s.machines} onClick={() => onVolumi('tutti')} />
      <StatCard label={t('Volumi di sistema protetti')} value={s.os_protected} tone={dati && s.os_protected > 0 ? 'ok' : undefined} />
      <StatCard label={t('Volumi di sistema NON protetti')} value={s.os_unprotected}
        tone={s.os_unprotected > 0 ? 'error' : dati ? 'ok' : undefined} onClick={() => onVolumi('os_scoperti')} />
      <StatCard label={t('Volumi protetti senza chiave in CENSO')} value={s.protected_without_key}
        tone={s.protected_without_key > 0 ? 'warn' : dati ? 'ok' : undefined} onClick={() => onVolumi('senza_chiave')}
        hint={d.settings.collect_keys ? undefined : t('la raccolta delle chiavi è spenta')} />
      <StatCard label={t('Chiavi custodite')} value={s.keys_active} />
      <StatCard label={t('Letture negli ultimi 30 giorni')} value={s.reads_30d}
        onClick={() => { window.location.hash = 'letture' }} />
      <StatCard label={t('Rotazioni in corso')} value={s.rotations_pending}
        onClick={() => { window.location.hash = 'rotazioni' }} />
    </div>
  )
}

/**
 * The Windows machines CENSO has no BitLocker detail for, split by what can be
 * done: an agent that has not reported yet (it updates itself and reports at
 * the next inventory — or now, on request), no agent but the probe's WinRM scan
 * knows whether BitLocker protects a volume, or nothing at all.
 */
function SenzaDati({ d, scrive, onChiesto }: { d: BitLockerOverview; scrive: boolean; onChiesto: () => void }) {
  const s = d.summary
  const [lavoro, setLavoro] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  if (s.windows_without_data === 0) return null
  async function chiedi() {
    setLavoro(true)
    setErrore(null)
    try {
      const r = await api.bitlockerRequestInventory()
      setEsito(t('Inventario chiesto a {n} agenti: i dati arrivano entro qualche minuto dagli agenti già aggiornati.', { n: r.requested }))
      onChiesto()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  return (
    <Card title={t('Macchine Windows senza dettaglio BitLocker ({n})', { n: s.windows_without_data })}>
      <ErrorBanner error={errore} />
      <div className="bitlocker-senza">
        <div className="bitlocker-senza__voce">
          <span className="bitlocker-senza__numero">{s.agents_waiting}</span>
          <div>
            <strong>{t('Con l’agente CENSO, dati non ancora arrivati')}</strong>
            <p className="field__hint">{t('L’agente si aggiorna da solo e manda lo stato BitLocker (e, con la raccolta accesa, le chiavi) al prossimo inventario. Puoi chiederlo subito.')}</p>
            {scrive && s.agents_waiting > 0 && (
              <button type="button" className="secondary" disabled={lavoro} onClick={() => void chiedi()}>
                {lavoro ? t('Chiedo…') : t('Chiedi l’inventario agli agenti')}
              </button>
            )}
            {esito && <p className="field__hint">{esito}</p>}
          </div>
        </div>
        <div className="bitlocker-senza__voce">
          <span className="bitlocker-senza__numero">{s.scan_protected + s.scan_unprotected}</span>
          <div>
            <strong>{t('Senza agente, stato dalla scansione WinRM')}</strong>
            <p className="field__hint">
              {t('{p} con BitLocker attivo, {n} senza. La scansione della sonda dice se un volume è protetto, non quali né le chiavi: per quelle serve l’agente.', { p: s.scan_protected, n: s.scan_unprotected })}
            </p>
            {s.scan_unprotected > 0 && (
              <Link to="/cis?attribute=disk_encryption&value=nessuna">{t('Vedi le macchine non protette')} →</Link>
            )}
          </div>
        </div>
        <div className="bitlocker-senza__voce">
          <span className="bitlocker-senza__numero">{s.no_info}</span>
          <div>
            <strong>{t('Nessuna informazione')}</strong>
            <p className="field__hint">{t('Né agente né scansione WinRM con credenziali: installa l’agente CENSO o aggiungi una credenziale Windows alla discovery.')}</p>
            <Link to="/inventory-agents">{t('Agenti d’inventario')} →</Link>
          </div>
        </div>
      </div>
    </Card>
  )
}

/** «Raccolta delle chiavi»: the two switches of the tenant (admins only). */
function Impostazioni({ s, admin, onSalvate }: { s: BitLockerSettings; admin: boolean; onSalvate: () => void }) {
  const [raccogli, setRaccogli] = useState(s.collect_keys)
  const [ruota, setRuota] = useState(s.rotate_after_reveal)
  const [salvo, setSalvo] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState(false)

  useEffect(() => {
    setRaccogli(s.collect_keys)
    setRuota(s.rotate_after_reveal)
  }, [s.collect_keys, s.rotate_after_reveal])

  const cambiato = raccogli !== s.collect_keys || ruota !== s.rotate_after_reveal
  const bloccato = !admin || salvo

  async function salva() {
    setSalvo(true)
    setErrore(null)
    setFatto(false)
    try {
      await api.saveBitLockerSettings({ collect_keys: raccogli, rotate_after_reveal: ruota })
      setFatto(true)
      onSalvate()
    } catch (e) {
      setErrore(e)
    } finally {
      setSalvo(false)
    }
  }

  return (
    <Card title={t('Raccolta delle chiavi')}>
      <p className="field__hint" style={{ marginTop: 0, marginBottom: 16 }}>
        {t('Ogni lettura di una chiave chiede un motivo, viene registrata (chi, quando, da dove e perché) e notificata per e-mail ai destinatari dell’evento «BitLocker» in Impostazioni. L’e-mail non contiene mai la chiave.')}
      </p>
      <div className="checkbox">
        <input id="bitlocker-raccogli" type="checkbox" checked={raccogli} disabled={bloccato}
          onChange={(e) => { setRaccogli(e.target.checked); setFatto(false) }} />
        <label htmlFor="bitlocker-raccogli">
          {t('Raccogli le chiavi di ripristino')}
          <small>
            {t('Gli agenti Windows inviano anche le password di ripristino dei volumi. Viaggiano sigillate nella busta dell’agente e sono conservate cifrate con la chiave del tenant: mai sul CI, mai nelle e-mail. Le chiavi API del tenant non possono leggerle. Spenta, CENSO conosce solo lo stato dei volumi.')}
          </small>
        </label>
      </div>
      <div className="checkbox">
        <input id="bitlocker-ruota" type="checkbox" checked={ruota} disabled={bloccato}
          onChange={(e) => { setRuota(e.target.checked); setFatto(false) }} />
        <label htmlFor="bitlocker-ruota">
          {t('Ruota la chiave dopo ogni lettura')}
          <small>
            {t('Una chiave letta non è più segreta. L’agente aggiunge una nuova password di ripristino e, appena CENSO l’ha custodita, toglie la vecchia: la macchina non resta mai senza una chiave custodita. Senza la raccolta delle chiavi la rotazione non può funzionare, perché la nuova chiave non arriverebbe mai a CENSO.')}
          </small>
        </label>
      </div>
      {ruota && !raccogli && (
        <div className="banner banner--warn">
          {t('Con la raccolta spenta la rotazione dopo la lettura non parte: accendi anche la raccolta delle chiavi.')}
        </div>
      )}
      <ErrorBanner error={errore} />
      <div className="toolbar" style={{ alignItems: 'center', marginBottom: 0 }}>
        {admin ? (
          <button type="button" onClick={() => void salva()} disabled={!cambiato || salvo}>
            {salvo ? t('Salvataggio…') : t('Salva')}
          </button>
        ) : (
          <span className="text-muted">{t('Solo un amministratore del tenant può cambiare queste impostazioni.')}</span>
        )}
        {fatto && <span className="badge badge--ok">{t('Salvato')}</span>}
        <span className="text-muted" style={{ fontSize: 13, marginLeft: 'auto' }}>
          {s.updated_at
            ? t('Ultima modifica: {quando}, da {chi}', { quando: formatDateTime(s.updated_at), chi: s.updated_by || '—' })
            : t('Mai modificate: raccolta spenta e rotazione dopo la lettura accesa, i valori predefiniti.')}
        </span>
      </div>
    </Card>
  )
}

// --------------------------------------------------------------- volumes

function Volumi({
  d,
  filtro,
  onFiltro,
  scrive,
  annullando,
  onMostra,
  onRuota,
  onAnnulla,
}: {
  d: BitLockerOverview
  filtro: FiltroVolumi
  onFiltro: (f: FiltroVolumi) => void
  scrive: boolean
  annullando: string | null
  onMostra: (k: BitLockerKeyInfo, v: BitLockerVolumeRow) => void
  onRuota: (k: BitLockerKeyInfo, v: BitLockerVolumeRow) => void
  onAnnulla: (r: BitLockerRotationInfo) => void
}) {
  const osScoperti = (v: BitLockerVolumeRow) => v.volume_type === 'OperatingSystem' && v.protection !== 'On'
  const senzaChiave = (v: BitLockerVolumeRow) => v.protection === 'On' && !v.escrowed
  const righe = d.volumes.filter((v) => filtro === 'tutti' || (filtro === 'os_scoperti' ? osScoperti(v) : senzaChiave(v)))
  const attive = (v: BitLockerVolumeRow) => v.keys.filter((k) => k.status === 'attiva')

  const colonne: Column<BitLockerVolumeRow>[] = [
    { key: 'ci_name', header: t('Macchina'), value: (v) => v.ci_name ?? '', render: (v) => <Macchina id={v.ci_id} nome={v.ci_name} /> },
    { key: 'mount', header: t('Volume'), mono: true, value: (v) => v.mount },
    { key: 'volume_type', header: t('Tipo'), value: (v) => etichettaTipoVolume(v.volume_type) },
    { key: 'protection', header: t('Protezione'), value: (v) => etichettaProtezione(v.protection), render: (v) => <BadgeProtezione protezione={v.protection} /> },
    { key: 'status', header: t('Stato'), value: (v) => etichettaStatoVolume(v.status), render: (v) => <span title={v.status}>{etichettaStatoVolume(v.status)}</span> },
    { key: 'method', header: t('Metodo'), value: (v) => v.method || '—' },
    { key: 'percent', header: t('Cifrato %'), type: 'number', value: (v) => v.percent, render: (v) => (v.percent === null || v.percent === undefined ? '—' : `${v.percent}%`) },
    { key: 'protectors', header: t('Protettori'), value: (v) => v.protectors.join(' '), render: (v) => (
      v.protectors.length === 0 ? <span className="text-muted">—</span>
        : <>{v.protectors.map((p) => <span key={p} className="chip">{p}</span>)}</>
    ) },
    { key: 'escrowed', header: t('Chiave in CENSO'), value: (v) => (v.escrowed ? t('sì') : t('no')), render: (v) => (
      v.escrowed ? <span className="badge badge--ok">{t('sì')}</span>
        : <span className={`badge badge--${v.protection === 'On' ? 'warn' : 'muted'}`}>{t('no')}</span>
    ) },
    { key: 'key_ids', header: t('ID chiave'), value: (v) => attive(v).map((k) => k.key_id).join(' '), render: (v) => (
      attive(v).length === 0 ? <span className="text-muted">—</span>
        : <>{attive(v).map((k) => <div key={k.id}><KeyId id={k.key_id} title={k.protector_id} /></div>)}</>
    ) },
    { key: 'rotation', header: t('Rotazione'), value: (v) => (v.rotation ? etichettaRotazione(v.rotation.status) : ''), render: (v) => <BadgeRotazione r={v.rotation} /> },
    { key: 'checked_at', header: t('Letto dall’agente'), type: 'date', hidden: true, value: (v) => v.checked_at ?? '', render: (v) => formatDateTime(v.checked_at) },
  ]

  const conta = (f: (v: BitLockerVolumeRow) => boolean) => d.volumes.filter(f).length

  if (d.volumes.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t('Nessun volume BitLocker ricevuto')}
          hint={t('I volumi arrivano con l’inventario dell’agente CENSO sulle macchine Windows. Le macchine Windows senza dati hanno un agente assente o più vecchio di questa versione.')}
        />
      </Card>
    )
  }

  return (
    <>
      <div className="segmented" role="tablist" aria-label={t('Filtro dei volumi')}>
        {([
          ['tutti', t('Tutti ({n})', { n: d.volumes.length })],
          ['os_scoperti', t('Sistema operativo non protetto ({n})', { n: conta(osScoperti) })],
          ['senza_chiave', t('Protetti senza chiave in CENSO ({n})', { n: conta(senzaChiave) })],
        ] as const).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={filtro === k}
            className={filtro === k ? 'segmented__on' : ''} onClick={() => onFiltro(k)}>
            {label}
          </button>
        ))}
      </div>
      <Card>
        <DataTable
          id="bitlocker-volumi"
          rows={righe}
          rowKey={(v) => `${v.ci_id}|${v.mount}`}
          columns={colonne}
          conteggio={{ nome: t('volumi') }}
          paginazione={{ predefinita: 50 }}
          emptyText={t('Nessun volume per questo filtro.')}
          expand={(v) => (
            <DettaglioVolume v={v} scrive={scrive} annullando={annullando}
              onMostra={(k) => onMostra(k, v)} onRuota={(k) => onRuota(k, v)} onAnnulla={onAnnulla} />
          )}
        />
      </Card>
    </>
  )
}

/** A volume opened: all its keys (also the superseded ones) and the last rotation. */
function DettaglioVolume({
  v,
  scrive,
  annullando,
  onMostra,
  onRuota,
  onAnnulla,
}: {
  v: BitLockerVolumeRow
  scrive: boolean
  annullando: string | null
  onMostra: (k: BitLockerKeyInfo) => void
  onRuota: (k: BitLockerKeyInfo) => void
  onAnnulla: (r: BitLockerRotationInfo) => void
}) {
  const r = v.rotation
  const rotazioneInCorso = Boolean(r && ['queued', 'running', 'waiting_escrow'].includes(r.status))
  const colonne: Column<BitLockerKeyInfo>[] = [
    { key: 'key_id', header: t('ID chiave'), value: (k) => k.key_id, render: (k) => <KeyId id={k.key_id} grande title={k.protector_id} /> },
    { key: 'status', header: t('Stato'), value: (k) => etichettaChiave(k.status), render: (k) => (
      <><BadgeChiave stato={k.status} />{k.superseded_at && <><br /><small className="text-muted">{formatDateTime(k.superseded_at)}</small></>}</>
    ) },
    { key: 'first_seen', header: t('Prima volta'), type: 'date', value: (k) => k.first_seen, render: (k) => formatDateTime(k.first_seen) },
    { key: 'last_seen', header: t('Ultima vista'), type: 'date', value: (k) => k.last_seen, render: (k) => formatDateTime(k.last_seen) },
    { key: 'revealed_count', header: t('Letture'), type: 'number', value: (k) => k.revealed_count },
    { key: 'last_revealed', header: t('Ultima lettura'), value: (k) => k.last_revealed_at ?? '', render: (k) => (
      k.last_revealed_at
        ? <>{k.last_revealed_by || '—'}<br /><small className="text-muted">{formatDateTime(k.last_revealed_at)}</small></>
        : <span className="text-muted">—</span>
    ) },
    ...(scrive ? [{
      key: 'azioni', header: '', sort: false, searchable: false, render: (k: BitLockerKeyInfo) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className={k.status === 'attiva' ? undefined : 'secondary'} onClick={() => onMostra(k)}>
            {t('Mostra la chiave')}
          </button>
          {k.status === 'attiva' && (
            <button type="button" className="secondary" onClick={() => onRuota(k)} disabled={rotazioneInCorso}
              title={rotazioneInCorso ? t('C’è già una rotazione in corso su questo volume.') : undefined}>
              {t('Ruota')}
            </button>
          )}
        </div>
      ),
    }] : []),
  ]

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {v.keys.length === 0 ? (
        <p className="text-muted" style={{ margin: 0 }}>
          {v.protection === 'On'
            ? t('CENSO non custodisce chiavi di questo volume: con la raccolta accesa arrivano al prossimo inventario dell’agente.')
            : t('Nessuna chiave custodita per questo volume.')}
        </p>
      ) : (
        <DataTable
          id="bitlocker-chiavi-volume"
          rows={v.keys}
          rowKey={(k) => k.id}
          columns={colonne}
          paginazione={false}
          strumenti={false}
        />
      )}
      {r && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <strong>{t('Ultima rotazione')}</strong>
            <BadgeRotazione r={r} />
            <span className="mono">{r.old_key_id} → {r.new_key_id || '…'}</span>
            <span className="text-muted" style={{ fontSize: 13 }}>
              {t('passo: {passo}', { passo: etichettaPasso(r.step) })} · {t('richiesta da {chi}', { chi: r.requested_by || '—' })} · {formatDateTime(r.created_at)}
              {r.finished_at ? ` → ${formatDateTime(r.finished_at)}` : ''}
            </span>
            {scrive && ANNULLABILI.includes(r.status) && (
              <button type="button" className="secondary" onClick={() => onAnnulla(r)} disabled={annullando === r.id}
                title={t('Annulla la rotazione')} style={{ marginLeft: 'auto' }}>
                {t('Annulla')}
              </button>
            )}
          </div>
          {r.reason && <p style={{ margin: '0 0 6px' }}><span className="text-muted">{t('Motivo')}:</span> {r.reason}</p>}
          <RegistroRotazione log={r.log} />
        </div>
      )}
    </div>
  )
}

function RegistroRotazione({ log }: { log: string }) {
  if (!log) return <p className="text-muted" style={{ margin: 0 }}>{t('Nessuna voce nel registro della rotazione.')}</p>
  return <pre className="json" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log}</pre>
}

// ------------------------------------------------------------- rotations

function Rotazioni({
  righe,
  scrive,
  annullando,
  onAnnulla,
}: {
  righe: BitLockerRotationInfo[]
  scrive: boolean
  annullando: string | null
  onAnnulla: (r: BitLockerRotationInfo) => void
}) {
  if (righe.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t('Nessuna rotazione')}
          hint={t('Una rotazione parte dopo la lettura di una chiave (se l’impostazione è accesa) o con «Ruota» nella sezione Volumi.')}
        />
      </Card>
    )
  }
  const colonne: Column<BitLockerRotationInfo>[] = [
    { key: 'ci_name', header: t('Macchina'), value: (r) => r.ci_name ?? '', render: (r) => <Macchina id={r.ci_id} nome={r.ci_name} /> },
    { key: 'mount', header: t('Volume'), mono: true, value: (r) => r.mount },
    { key: 'chiavi', header: t('ID chiave'), value: (r) => `${r.old_key_id} ${r.new_key_id}`, render: (r) => (
      <span className="mono">{r.old_key_id} → {r.new_key_id || '…'}</span>
    ) },
    { key: 'step', header: t('Passo'), value: (r) => etichettaPasso(r.step) },
    { key: 'status', header: t('Stato'), value: (r) => etichettaRotazione(r.status), render: (r) => <BadgeRotazione r={r} /> },
    { key: 'requested_by', header: t('Richiesta da'), value: (r) => r.requested_by || '—' },
    { key: 'reason', header: t('Motivo'), value: (r) => r.reason || '—' },
    { key: 'created_at', header: t('Creata'), type: 'date', value: (r) => r.created_at ?? '', render: (r) => formatDateTime(r.created_at) },
    { key: 'finished_at', header: t('Finita'), type: 'date', value: (r) => r.finished_at ?? '', render: (r) => formatDateTime(r.finished_at) },
    ...(scrive ? [{
      key: 'azioni', header: '', sort: false, searchable: false, render: (r: BitLockerRotationInfo) => (
        ANNULLABILI.includes(r.status) ? (
          <button type="button" className="secondary" onClick={() => onAnnulla(r)} disabled={annullando === r.id}
            title={t('Annulla la rotazione')}>
            {t('Annulla')}
          </button>
        ) : null
      ),
    }] : []),
  ]
  return (
    <Card>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Due passi: l’agente aggiunge una nuova password di ripristino (e prova a copiarla in Active Directory o Entra ID), poi — quando CENSO l’ha custodita — toglie la vecchia. Si annullano le rotazioni in coda, in attesa della nuova chiave o non riuscite.')}
      </p>
      <DataTable
        id="bitlocker-rotazioni"
        rows={righe}
        rowKey={(r) => r.id}
        columns={colonne}
        initialSort={{ key: 'created_at', dir: 'desc' }}
        conteggio={{ nome: t('rotazioni') }}
        paginazione={{ predefinita: 50 }}
        expand={(r) => <RegistroRotazione log={r.log} />}
      />
    </Card>
  )
}

// ---------------------------------------------------------------- reads

function Letture({ righe }: { righe: BitLockerAccessRow[] }) {
  if (righe.length === 0) {
    return (
      <Card>
        <EmptyState title={t('Nessuna lettura')} hint={t('Ogni chiave mostrata dalla console compare qui: chi, quando, da dove e perché.')} />
      </Card>
    )
  }
  const colonne: Column<BitLockerAccessRow>[] = [
    { key: 'at', header: t('Quando'), type: 'date', value: (a) => a.at, render: (a) => formatDateTime(a.at) },
    { key: 'actor', header: t('Chi'), value: (a) => a.actor || '—' },
    { key: 'ci_name', header: t('Macchina'), value: (a) => a.ci_name, render: (a) => <Macchina id={a.ci_id} nome={a.ci_name} /> },
    { key: 'mount', header: t('Volume'), mono: true, value: (a) => a.mount },
    { key: 'key_id', header: t('ID chiave'), mono: true, value: (a) => a.key_id },
    { key: 'reason', header: t('Motivo'), value: (a) => a.reason },
    { key: 'source_ip', header: t('Indirizzo IP'), mono: true, value: (a) => a.source_ip || '—' },
  ]
  return (
    <Card>
      <DataTable
        id="bitlocker-letture"
        rows={righe}
        rowKey={(a) => a.id}
        columns={colonne}
        initialSort={{ key: 'at', dir: 'desc' }}
        conteggio={{ nome: t('letture') }}
        paginazione={{ predefinita: 50 }}
        sourceNote={righe.length >= 500 ? t('Le ultime 500 letture.') : undefined}
      />
    </Card>
  )
}
